import { ResourceJson, DictionaryJson, ResourcesRecord } from './models-internal';

export class ResourceMap {
  private readonly resources = new Map<string, Resource>();

  constructor(
    resourcesRecord: ResourcesRecord
  ) {
    for (const [lang, resourceJson] of Object.entries(resourcesRecord)) {
      const resource = new Resource(lang, resourceJson);
      this.resources.set(lang, resource);
    }
  }

  public getAvailableLanguages() {
    return [...this.resources.keys()]
  }

  public getPhrase(lookup: { lang: string, namespace: string, key: string }) {
    const { lang, namespace, key } = lookup;
    const dictionary = this.getDictionary({ lang, namespace });
    return dictionary?.get(key);
  }

  public getDictionary(lookup: { lang: string, namespace: string }) {
    const { lang, namespace } = lookup;
    const resource = this.resources.get(lang);
    return resource?.get(namespace);
  }

  public load(resourcesRecord: ResourcesRecord) {
    for (const [lang, resourceJson] of Object.entries(resourcesRecord)) {
      const exists = this.resources.get(lang);
      if (exists) {
        exists.load(resourceJson);
      } else {
        const resource = new Resource(lang, resourceJson);
        this.resources.set(lang, resource);
      }
    }
  }
}

export class Resource {
  /**
   * A list of dictionaries, associated with the namespace that owns them.
   */
  private readonly dictionaries = new Map<string, Dictionary>();
  public readonly lang: string;

  constructor(
    lang: string,
    resourceJson: ResourceJson,
  ) {
    this.lang = lang.toLowerCase();
    this.load(resourceJson);
  }

  /** Get a dictionary from this resource. */
  public get(lang: string) {
    return this.dictionaries.get(lang.toLowerCase());
  }

  public load(resourceJson: ResourceJson) {
    for (const [namespace, dictionaryJson] of Object.entries(resourceJson)) {
      const dictionary = new Dictionary(dictionaryJson);
      this.dictionaries.set(namespace, dictionary);
    }
  }
}

export class Dictionary {
  private readonly map: Map<string, string>;

  constructor(dictionaryJson: DictionaryJson) {
    this.map = this.flatten(dictionaryJson);
  }

  /** Get a phrase from this dictionary. */
  public get(key: string) {
    return this.map.get(key);
  }

  /** Get this entire dictionary as JSON. */
  public getAsJson() {
    return Object.fromEntries(this.map.entries() ?? []);
  }

  /** Flatten this dictionary. */
  private flatten(json: DictionaryJson): Map<string, string> {
    const flat = new Map<string, string>();

    for (const key in json) {
      if (!Object.hasOwn(json, key)) continue;
      const text = json[key];

      if (typeof text === 'string') {
        flat.set(key, text);
      } else {
        const next = this.flatten(text);
        for (const [key0, value0] of next.entries()) {
          flat.set(`${key}.${key0}`, value0);
        }
      }
    }

    return flat;
  }
}
